import { Button, FloatingIndicator, Group, NavLink, Stack, Text, Tooltip } from "@mantine/core";
import { useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import Icon from "src/components/common/Icon";
import type { ILayoutLink } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import { getLayoutLinkIcon } from "src/routing/navigationIcons";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

/**
 * Defines the inputs for this navigation presentation.
 */
interface INavMenuProps {
    menuItems?: ILayoutLink[];
    mobile?: boolean;
    onNavigate?: () => void;
}

/**
 * Finds the longest allowed href that prefixes the pathname without mutating the input list.
 *
 * Assumptions: Matching uses raw string prefixes, not route-segment boundaries.
 */
export const getSelectedLayoutHref = (pathname: string, menuItems: ILayoutLink[]) =>
    menuItems
        .filter(menuItem => pathname.startsWith(menuItem.href))
        .sort((left, right) => right.href.length - left.href.length)[0]?.href;

/**
 * Renders permitted top-level navigation with separate mobile and desktop presentations.
 *
 * Postconditions: Search is excluded from this menu. The longest matching href selects the
 * indicator. Mobile clicks run the item's action before onNavigate; desktop clicks run its action.
 */
const NavMenu = ({ menuItems = [], mobile = false, onNavigate }: INavMenuProps) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();
    const allowedItems = useMemo(
        () =>
            filterLayoutLinksByPermission(permissions, menuItems).filter(item =>
                item.href !== "/search"
            ),
        [menuItems, permissions]
    );
    const selectedHref = getSelectedLayoutHref(location.pathname, allowedItems);
    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlRefs, setControlRefs] = useState<Record<string, HTMLElement | null>>({});
    const controlRefCallbacks = useRef<Record<string, (node: HTMLElement | null) => void>>({});

    /**
     * Reuses a callback per href and records non-null mounted elements for the indicator.
     *
     * Postconditions: Repeated registration of the same node keeps state unchanged; null callbacks
     * are ignored and do not remove stored elements.
     */
    const getControlRef = (href: string) => {
        const existingCallback = controlRefCallbacks.current[href];

        if (existingCallback) {
            return existingCallback;
        }

        /**
         * Requests the current element mapping only when a non-null node changes.
         */
        const callback = (node: HTMLElement | null) => {
            if (!node) {
                return;
            }

            setControlRefs(current =>
                current[href] === node ? current : { ...current, [href]: node }
            );
        };

        controlRefCallbacks.current[href] = callback;
        return callback;
    };

    if (mobile) {
        return (
            <Stack gap={4}>
                {allowedItems.map(item => (
                    <NavLink
                        key={item.href}
                        component={item.actionFn ? "button" : RouterLink}
                        to={item.actionFn ? undefined : item.href}
                        target={item.newTab ? "_blank" : undefined}
                        onClick={() => {
                            item.actionFn?.();
                            onNavigate?.();
                        }}
                        active={selectedHref === item.href}
                        label={item.label}
                        leftSection={
                            <Icon
                                icon={getLayoutLinkIcon(item)}
                                fixedWidth
                            />
                        }
                    />
                ))}
            </Stack>
        );
    }

    return (
        <Group
            ref={setRootRef}
            h="100%"
            gap={0}
            visibleFrom="sm"
            wrap="nowrap"
            align="stretch"
            style={{ position: "relative" }}
        >
            <FloatingIndicator
                target={selectedHref ? controlRefs[selectedHref] : null}
                parent={rootRef}
                transitionDuration={200}
                style={{
                    backgroundColor: "var(--mantine-primary-color-light)",
                    pointerEvents: "none"
                }}
            />
            {allowedItems.map(item => (
                <Tooltip
                    key={item.href}
                    label={item.label}
                    withArrow
                >
                    <Button
                        ref={getControlRef(item.href)}
                        component={item.actionFn ? "button" : RouterLink}
                        to={item.actionFn ? undefined : item.href}
                        target={item.newTab ? "_blank" : undefined}
                        onClick={item.actionFn}
                        variant="subtle"
                        color="primary"
                        px={{ base: "sm", lg: "md" }}
                        aria-label={item.label}
                        bdrs={0}
                        h="100%"
                        styles={{
                            root: {
                                border: 0,
                                position: "relative",
                                zIndex: 1,
                                backgroundColor:
                                    selectedHref === item.href ? "transparent" : undefined
                            }
                        }}
                    >
                        <Group
                            gap="xs"
                            wrap="nowrap"
                        >
                            <Icon icon={getLayoutLinkIcon(item)} />
                            <Text
                                span
                                visibleFrom="lg"
                            >
                                {item.label}
                            </Text>
                        </Group>
                    </Button>
                </Tooltip>
            ))}
        </Group>
    );
};

export default NavMenu;
